/**
 * printUnits — how a printed document spells a line's unit of measure.
 *
 * Owner 2026-09-29: the thermal receipts printed "2 x P1,890.00" with no unit
 * while the packing slip said "2 BAGS". Every print renderer (PrintEngine's
 * HTML thermal / dot-matrix, PrintOps' terminal text mode) takes the unit
 * from here so they cannot drift apart again.
 *
 * Printed units are UPPER CASE: the catalog is free text ("Sachet", "bags")
 * and old documents keep the spelling they were saved with.
 *
 * Pure: no React, no DOM.
 */
import { unitSummary } from './transferCard';

/** The unit to print for one line, upper-cased, or '' when the line has none. */
export function lineUnit(item) {
  return String((item && (item.unit || item.uom)) || '').trim().toUpperCase();
}

/** Quantity as printed: at most 3 decimals (0.125 KG is real), no trailing
 *  zeros ("2", "2.5"). Same rounding as the transfer card's unit totals. */
export function fmtQty(qty) {
  const n = parseFloat(qty) || 0;
  return parseFloat(n.toFixed(3)).toString();
}

/** "2 BAGS" — or just "2" when the line has no unit. */
export function qtyWithUnit(qty, item) {
  const unit = lineUnit(item);
  return unit ? `${fmtQty(qty)} ${unit}` : fmtQty(qty);
}

// ── Per-category summary (owner 2026-09-29) ─────────────────────────────────
// "Pesticide: 10 BAGS, 3 L, 5 BOT" at the bottom of a document, replacing the
// old "TOTAL PIECES 18" that added bags, liters and bottles into one number.
// The grouping is the transfer card's unitSummary (categories most-lines
// first; a plural joins its singular when both appear — the catalog cleanup
// rule); this only adapts it to paper: UPPER CASE, a line without a unit
// counts as PC (as the packing slip always did), no category → OTHERS.

const NO_CATEGORY = 'OTHERS';

/**
 * items: document lines. qtyOf(item) → the quantity to count (defaults to
 * quantity/qty). Lines with a zero quantity (removed lines) are skipped.
 *
 * Returns { lines, categories: [{ name, lines, units: ['10 BAGS', '3 L'] }] }
 */
export function categorySummary(items, qtyOf) {
  const q = qtyOf || ((i) => (i.quantity ?? i.qty));
  const rows = (items || []).map((i) => ({
    qty: parseFloat(q(i)) || 0,
    unit: lineUnit(i) || 'PC',
    category: String(i.category || '').trim().toUpperCase() || NO_CATEGORY,
  }));
  const s = unitSummary(rows);
  return {
    lines: s.lines,
    categories: s.categories.map((c) => ({
      name: c.name === 'Uncategorized' ? NO_CATEGORY : c.name,
      lines: c.lines,
      units: c.units.map((u) => `${fmtQty(u.qty)} ${u.unit.toUpperCase()}`),
    })),
  };
}

const itemsLabel = (n) => `${n} ${n === 1 ? 'item' : 'items'}`;

/**
 * 58mm thermal block. `showCount` off when the document already prints its
 * own line count (Branch Transfer "Items", Stock Request "Lines").
 * Each unit is kept whole on wrap ("10 BAGS" never splits across lines).
 */
export function categorySummaryThermalHTML(items, { qtyOf, showCount = true } = {}) {
  const s = categorySummary(items, qtyOf);
  if (!s.categories.length) return '';
  let h = '<div class="sep"></div><div class="cat-summary" style="font-size:11px;line-height:1.3">';
  h += `<div style="display:flex;justify-content:space-between;font-weight:bold;margin-bottom:2px"><span>ITEM SUMMARY</span><span>${showCount ? itemsLabel(s.lines) : ''}</span></div>`;
  for (const c of s.categories) {
    h += `<div style="font-weight:bold;margin-top:2px">${c.name} (${c.lines})</div>`;
    h += `<div style="padding-left:8px">${c.units.map((u) => `<span style="white-space:nowrap">${u}</span>`).join(', ')}</div>`;
  }
  return h + '</div>';
}

/** Dot-matrix block — a small table: Category | Items | Quantities. */
export function categorySummaryDotMatrixHTML(items, { qtyOf, showCount = true } = {}) {
  const s = categorySummary(items, qtyOf);
  if (!s.categories.length) return '';
  let h = '<table class="dm-meta-grid" style="margin-top:6px;">';
  h += `<tr><th style="width:34%;text-align:left;">ITEM SUMMARY${showCount ? ` — ${itemsLabel(s.lines)}` : ''}</th><th style="width:10%;">Items</th><th style="text-align:left;">Quantities</th></tr>`;
  for (const c of s.categories) {
    h += `<tr><td style="font-weight:bold;">${c.name}</td><td class="c">${c.lines}</td><td>${c.units.join(', ')}</td></tr>`;
  }
  return h + '</table>';
}

/**
 * Plain-text lines for the terminal's text mode (ASCII only, no word-wrap of
 * its own): [{ text, bold }]. Units wrap at `width` on the ", " between them.
 */
export function categorySummaryTextLines(items, { qtyOf, showCount = true, width = 30 } = {}) {
  const s = categorySummary(items, qtyOf);
  if (!s.categories.length) return [];
  const out = [];
  const head = 'ITEM SUMMARY';
  const count = showCount ? itemsLabel(s.lines) : '';
  out.push({ text: count ? head + ' '.repeat(Math.max(1, width - head.length - count.length)) + count : head, bold: true });
  for (const c of s.categories) {
    out.push({ text: `${c.name} (${c.lines})`, bold: true });
    // Indent 2; a wrapped line ends in "," so it may use width - 3 for units.
    let line = '';
    for (const u of c.units) {
      if (!line) { line = u; continue; }
      if (2 + line.length + 2 + u.length + 1 <= width) { line += `, ${u}`; continue; }
      out.push({ text: `  ${line},`, bold: false });
      line = u;
    }
    if (line) out.push({ text: `  ${line}`, bold: false });
  }
  return out;
}
